import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { DataService, Dialog, GetFacetListQuery, ItemOf, NotificationService } from '@vendure/admin-ui/core';
import { SharedModule } from '@vendure/admin-ui/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { startWith } from 'rxjs/operators';
import gql from 'graphql-tag';
import { lastValueFrom } from 'rxjs';

const GET_FACETS = gql`
    query GetFacets {
        facets {
            items {
                id
                code
                name
            }
        }
    }
`;

interface AssignFacetToChannelBulk {
    assignFacetToChannelBulk: boolean;
}

// Correct type - using Facet instead of FacetValue
type Facet = ItemOf<GetFacetListQuery, 'facets'>;

@Component({
    standalone: true,
    imports: [SharedModule, NgSelectModule],
    selector: 'vdr-assign-products-to-facets-dialog',
    templateUrl: './assign-facets-to-bulk-channels-dialog.component.html',
    styleUrls: ['./assign-facets-to-bulk-channels-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignProductsToFacetsDialog implements OnInit, Dialog<any> {
    facetsControl = new UntypedFormControl([]);
    selectedFacets: Facet[] = [];
    availableFacets: Facet[] = [];
    resolveWith: (result?: any) => void;
    selectedFacetIds: string[] = [];

    // assigned by ModalService.fromComponent() call
    productIds: string[];
    currentFacetIds: string[];

    constructor(
        private dataService: DataService,
        private notificationService: NotificationService,
        private changeDetectorRef: ChangeDetectorRef // Inject ChangeDetectorRef
    ) {}

    selectFacets(facetIds: string[]) {
        this.selectedFacets = facetIds
            .map(id => this.availableFacets.find(f => f.id === id)!)
            .filter(Boolean) as Facet[];
        this.selectedFacetIds = this.selectedFacets.map(f => f.id);
    }

    async ngOnInit() {
        const facetConn = await lastValueFrom(
            this.dataService
                .query<{ facets: { items: Facet[] } }, {}>(GET_FACETS, {})
                .mapSingle(d => d.facets),
        );
        this.availableFacets = facetConn.items;

        // Automatically select all facets
        const allFacetIds = this.availableFacets.map(facet => facet.id);

        // Set the value of facetsControl to the list of all facet IDs
        this.facetsControl.setValue(allFacetIds);

        // Update the selectedFacetIds directly
        this.selectedFacetIds = allFacetIds;

        // Automatically select the facets
        this.selectFacets(allFacetIds);

        // Manually trigger change detection after data is loaded
        this.changeDetectorRef.detectChanges();

        // Subscribe to valueChanges for any further updates if needed
        this.facetsControl.valueChanges
            .pipe(startWith(allFacetIds))
            .subscribe(ids => this.selectFacets(ids));
    }

    assign() {
        const result$ = this.dataService
            .mutate<AssignFacetToChannelBulk>(
                gql`
                    mutation AssignFacetToChannelBulk {
                        assignFacetToChannelBulk
                    }
                `,
            )
            .subscribe(
                response => {
                    const apiResponseStatus: boolean = response.assignFacetToChannelBulk;
                    if (apiResponseStatus === true) {
                        this.notificationService.success(_('catalog.assign-facets-to-channels-bulk-success'));
                        this.resolveWith(this.selectedFacetIds);
                    } else {
                        this.notificationService.error(_('catalog.assign-facets-to-channels-bulk-error'));
                        this.resolveWith(false);
                    }
                },
                error => {
                    console.error('Error occurred:', error);
                    this.notificationService.error(_('catalog.assign-facets-to-channels-bulk-error'));
                    this.resolveWith(false);
                },
            );
    }

    removeFacet(facetId: string) {
        // Remove the facet ID from selectedFacetIds
        this.selectedFacetIds = this.selectedFacetIds.filter(id => id !== facetId);
        
        // Also update selectedFacets based on the new selectedFacetIds
        this.selectFacets(this.selectedFacetIds);
    }
    
    cancel() {
        this.resolveWith();
    }
}
